import type { LocalJob } from "./localTypes";

export type DataType =
  | "ID"
  | "Text"
  | "Boolean"
  | "Number"
  | "State"
  | "Date"
  | "Duration"
  | "Tag"
  | "User"
  | "Account"
  | "Classification";

const fieldList = [
  {
    fieldName: "id",
    dataType: "ID",
    isSortable: true,
  },
  {
    fieldName: "account",
    dataType: "Account",
    isSortable: false,
  },
  {
    fieldName: "state",
    dataType: "State",
    isSortable: true,
  },
  {
    fieldName: "creator",
    dataType: "User",
    isSortable: false,
  },
  {
    fieldName: "created",
    dataType: "Date",
    isSortable: true,
  },
  {
    fieldName: "isProcessAgent",
    dataType: "Boolean",
    isSortable: false,
  },
  {
    fieldName: "tags",
    dataType: "Tag",
    isSortable: false, // The API does allows sorting by tag, but I'm not sure if it's useful, so setting to false for now
  },
  {
    fieldName: "name",
    dataType: "Text",
    isSortable: true,
  },
  {
    fieldName: "elapsed",
    dataType: "Duration",
    isSortable: false,
  },
  {
    fieldName: "endedOn",
    dataType: "Date",
    isSortable: false,
  },
  {
    fieldName: "gpu",
    dataType: "Text",
    isSortable: true,
    orderField: "resources.gpu",
  },
  {
    fieldName: "replicas",
    dataType: "Text",
    isSortable: true,
    orderField: "resources.replicas",
  },
] as const;

export type Fields = {
  [FieldName in (typeof fieldList)[number]["fieldName"]]: Extract<
    (typeof fieldList)[number],
    { fieldName: FieldName }
  >;
};

export type Field = Fields[keyof Fields];

export type ResourceField<ResourceType extends object> = {
  fieldName: keyof ResourceType;
} & Field;

export type FieldLabels<ResourceType extends object> = {
  [FieldName in keyof ResourceType &
    (typeof fieldList)[number]["fieldName"]]: string;
};

const _fields = {} as Record<keyof Fields, Field>;
for (const field of fieldList) {
  _fields[field.fieldName] = field;
}
export const fields = _fields as Fields;

export function isKnownField(fieldName: string): fieldName is keyof Fields {
  return fieldName in fields;
}

export const defaultFieldLabels = {
  id: "ID",
  account: "Account",
  state: "State",
  creator: "Submitted by",
  created: "Created",
  isProcessAgent: "Process Agent",
  tags: "Tags",
  name: "Name",
  elapsed: "Elapsed",
  endedOn: "End time",
  gpu: "GPUs",
  replicas: "Replicas",
} as const;

export function getDefaultSortOrder(field: Field): "asc" | "desc" {
  switch (field.dataType) {
    case "Date":
      return "desc";
    case "Boolean":
      return "desc";
    default:
      return "asc";
  }
}

export const jobFieldLabels: FieldLabels<LocalJob> = {
  ...defaultFieldLabels,
  name: "Job name",
};

export const defaultSortField = fields["created"];
